import {
  SortDirection,
  StoredFileFragment,
} from '@warebee/frontend/data-access-api-graphql';
import { LayoutDataRow } from '@warebee/shared/export-converter';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValueLoadable } from 'recoil';
import { DatasetDataViewProps } from '../common/types';
import DatasetTable from '../components/DatasetTable';
import useWarehouseFilesTableConfig from './hooks/useWarehouseFilesTableConfig';
import { warehouseFilesData } from './store/warehouseFiles.state';

const WarehouseFilesDataTable: React.FC<
  DatasetDataViewProps<LayoutDataRow>
> = props => {
  const { t } = useTranslation('dataset');
  const dataLoadable = useRecoilValueLoadable(warehouseFilesData);
  const columnsConfig = useWarehouseFilesTableConfig();

  const [tableState, setTableState] = useState<{
    searchValues: Record<string, string>;
    sortValues: Partial<Record<string, SortDirection>>;
  }>({
    searchValues: {},
    sortValues: { lastModified: SortDirection.DESC }, // Default sort by date, most recent first
  });

  const rawData =
    dataLoadable.state === 'hasValue' ? dataLoadable.contents : [];
  const isLoading = dataLoadable.state === 'loading';

  // Filter the data based on search values
  const filteredData = useMemo(() => {
    if (_.isEmpty(tableState.searchValues)) {
      return rawData;
    }

    return rawData.filter(row => {
      return _.every(tableState.searchValues, (value, key) => {
        if (!value) return true;
        const rowValue = _.get(row, key)?.toString()?.toLowerCase();
        return rowValue?.includes(value.toLowerCase());
      });
    });
  }, [rawData, tableState.searchValues]);

  // Sort the filtered data
  const sortedData = useMemo(() => {
    // Always ensure lastModified is in the sort values
    const sortValues = {
      ...tableState.sortValues,
      lastModified: tableState.sortValues.lastModified || SortDirection.DESC,
    };

    const sortFields = Object.keys(sortValues);
    const sortDirections = Object.values(sortValues).map(dir =>
      dir === SortDirection.ASC ? 'asc' : 'desc',
    );

    // Custom sorting function to handle raw values
    const customSort = (data: StoredFileFragment[]) => {
      return _.orderBy(
        data,
        sortFields.map(field => {
          // For size and lastModified fields, use raw numeric values
          if (field === 'size' || field === 'lastModified') {
            return (item: StoredFileFragment) => {
              const value = _.get(item, field);
              return typeof value === 'number' ? value : 0;
            };
          }
          return field;
        }),
        sortDirections,
      );
    };

    return customSort(filteredData);
  }, [filteredData, tableState.sortValues]);

  const totalCount = sortedData.length;

  return (
    <DatasetTable<StoredFileFragment>
      hasCounter
      hideScreenTitle={props.hideScreenTitle}
      subtitle={t`Files`}
      title={props.title}
      isSticky
      id={props?.id}
      columnsConfig={columnsConfig}
      keyFields={['warehouseId', 'name']}
      data={sortedData}
      onSearch={searchValues => {
        setTableState(prev => ({ ...prev, searchValues }));
      }}
      onSort={sortValues => {
        // Ensure lastModified is always in the sort values
        const newSortValues = {
          ...sortValues,
          lastModified: sortValues.lastModified || SortDirection.DESC,
        };
        setTableState(prev => ({
          ...prev,
          sortValues: newSortValues,
        }));
      }}
      searchValues={tableState.searchValues}
      sortBy={tableState.sortValues}
      totalCount={totalCount}
      isLoading={isLoading}
      onLoadNext={_.noop}
    />
  );
};

export default WarehouseFilesDataTable;
